import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImageCropperComponent } from 'src/app/dialog/image-cropper/image-cropper.component';
import { STATUS } from 'src/app/enums/status.enum';
import { GroupAge } from 'src/app/models/group-age';
import { Nationality } from 'src/app/models/nationality.model';
import { TicketModel } from 'src/app/models/ticket';
import { ApiService } from 'src/app/services/api.service';
import { DataService } from 'src/app/services/data.service';
import { ImageValidatorService } from 'src/app/services/image-validator.service';
import { LoadingService } from 'src/app/services/loading.service';
import { UtilityService } from 'src/app/services/utility.service';
import { fadeOp, fadeOpInOut } from 'src/app/util/animations.util';
import { SnackbarMessageComponent } from '../snackbar-message/snackbar-message.component';

@Component({
  selector: 'app-ticket-card',
  templateUrl: './ticket-card.component.html',
  styleUrls: ['./ticket-card.component.css'],
  animations: [
    fadeOp,
    fadeOpInOut
  ]
})
export class TicketCardComponent implements OnInit, OnDestroy {
  // @Input() form: FormGroup;
  @Input() groupAges!: GroupAge[];
  @Input() nationalities!: Nationality[];
  @Input() filteredNationalityArray!: Nationality[];
  @Output() labelover: EventEmitter<HTMLElement> = new EventEmitter();
  @Output() labelout: EventEmitter<HTMLElement> = new EventEmitter();
  @Input() control!: FormGroup;
  @Input() values!: TicketModel
  @Input() container!: HTMLElement
  @Input() formGroup!: FormGroup;
  @Input() enableRemove!: boolean;
  @Input() filePickerKey!: string | number;
  @Input() fileTicketKey!: number;
  @Input() isKohker!: boolean;
  @Input() index!: number;
  @Input() lang!: number;

  @Output() remove: EventEmitter<void> = new EventEmitter();
  isOpenTooltip: boolean = false;
  imageSource!: SafeResourceUrl;

  nationalityFilterCtrl: FormControl = new FormControl();
  protected _onDestroy = new Subject<void>();

  isImageLoading!: boolean;

  private url!: string;

  constructor(
    private dialog: MatDialog,
    private api: ApiService,
    private utility: UtilityService,
    private loadingService: LoadingService,
    private sanitizer: DomSanitizer,
    private imageValidator: ImageValidatorService,
    private snackbar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.nationalityFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.filterNationalities();
    });
    this.isImageLoading = true;
    this.loadImage();
  }

  getFormattedTicketTitle(): string {
    return `- ${this.fileTicketKey + 1}`;
  }

  loadImage() {
    if (!this.photo.value) {
      this.isImageLoading = false;
      return;
    }
    this.api.getFileByID(this.photo.value)
      .subscribe(
        response => {
          if (response.constructor == Blob) {
            URL.revokeObjectURL(this.url);
            this.url = URL.createObjectURL(response);
            this.imageSource = this.sanitizer.bypassSecurityTrustResourceUrl(this.url);
            this.formGroup.patchValue({
              photoURL: this.imageSource
            });
            this.isImageLoading = false;
          }
        },
      );
  }
  onPickPhoto(e: Event): void {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!/^image\/*/.test(file.type)) return;

    const reader: FileReader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.loadingService._clearLoading();

      this.dialog.open(ImageCropperComponent, {
        width: '880px',
        disableClose: true,
        autoFocus: false,
        data: {
          image: e.target?.result,
          isShow: true
        }
      })
        .afterClosed()
        .subscribe((canvas: HTMLCanvasElement) => {
          input.value = "";
          if (!canvas) return;

          const DATA_URL = canvas.toDataURL(file.type, 1);
          canvas.remove();

          const imageCropped = this.utility.b64toBlob(DATA_URL, file.type);

          // Handle both success and failure
          this.imageValidator.faceDetect(DATA_URL)
            .then(() => {
              this.isImageLoading = true;
              this.api.uploadFile(imageCropped).subscribe(
                response => {
                  switch (response.status) {
                    case STATUS.ERROR_CLIENT:
                      this.openErrorSnackbar('MSG.PHOTO_UNSUPPORTED');
                      this.loadImage();
                      break;
                    case STATUS.ERROR_SERVER:
                      this.openErrorSnackbar('MSG.SERVER_ERROR');
                      this.loadImage();
                      break;
                    case STATUS.SUCCESS:
                      this.photo.patchValue(response.data._id);
                      this.loadImage();
                      break;
                  }
                },
                err => {
                  console.error('Upload failed', err);
                  this.openErrorSnackbar('MSG.SERVER_ERROR');
                  this.loadImage();
                }
              );
            })
            .catch((err) => {
              console.error('Face detection failed', err);
              this.openErrorSnackbar('No face detected. Please try again.');
            });
        });
    };

    this.loadingService._setLoading();
    reader.readAsDataURL(file);
  }

  clearImage() {
    this.imageSource = "";
  }


  // get tooltipImage(): SafeResourceUrl | void {
  //   if (this.dataService.tooltipImage) {
  //     return this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(this.dataService.tooltipImage));
  //   }
  // }

  get value(): TicketModel {
    return this.formGroup.value;
  }

  get groupAge(): FormControl {
    return this.formGroup.get('group_age') as FormControl;
  }

  get gender(): FormControl {
    return this.formGroup.get('sex') as FormControl;
  }

  get nationality(): FormControl {
    return this.formGroup.get('nationality') as FormControl;
  }

  get photo(): FormControl {
    return this.formGroup.get('photo') as FormControl;
  }

  get currentLang(): string {
    return this.utility.getLang();
  }

  getNationalityName(nationality: any) {
    let name;
    switch (this.currentLang) {
      case 'kr':
        name = nationality?.name.kr;
        break;
      case 'ch':
        name = nationality?.name.ch;
        break;
      case 'jp':
        name = nationality?.name.jp;
        break;

      default:
        name = nationality?.name.en;
        break;
    }
    return name;
  }

  openErrorSnackbar(message: string /* Localize String */): void {
    this.snackbar.openFromComponent(SnackbarMessageComponent, {
      data: {
        message
      },
      duration: 2000,
      panelClass: 'bg-warn'
    })
  }

  ngOnDestroy(): void {
    URL.revokeObjectURL(this.url);
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  resetFilter() {
    this.filteredNationalityArray = this.nationalities;
  }

  openTooltip(e: Event): void {
    if (e.cancelable) {
      e.preventDefault();
    }
    // const label = e.currentTarget as HTMLLabelElement;
    this.isOpenTooltip = true;
  }
  closeTooltip(e: Event, isTouch?: boolean): void {
    if (e.cancelable) {
      e.preventDefault();
    }
    const label = e.currentTarget as HTMLLabelElement;
    if (isTouch) {
      label.click();
    }
    this.isOpenTooltip = false;
  }

  filterNationalities() {
    if (!this.nationalities) {
      return;
    }
    // get the search keyword
    let search = this.nationalityFilterCtrl.value;

    if (!search) {
      return this.filteredNationalityArray = this.nationalities;
    } else {
      search = search.toLowerCase();
    }
    this.filteredNationalityArray = this.nationalities.filter(item => item.nationality.toLowerCase().indexOf(search) > -1)
  }
}
